import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Send, Save, Clock, Mail, Eye, AlertCircle, Users, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useCampaign, useCreateCampaign, useUpdateCampaign,
  useAudiences, useTemplates, useSendTestEmail,
  useScheduleCampaign, useSendNow, useEstimateRecipients,
} from '@/hooks/useNewsletter';
import {
  DEFAULT_THEME, CAMPAIGN_STATUS_LABELS,
  type Block, type TemplateTheme, type Campaign,
} from '@/types/newsletter';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { EmailEditor } from '@/components/newsletter/EmailEditor';

type Step = 'setup' | 'design' | 'audience' | 'review';

export function CampaignEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'newsletter', 'write') : false;

  const { data: campaign, isLoading, error } = useCampaign(isNew ? undefined : id);
  const { data: audiences } = useAudiences();
  const { data: templates } = useTemplates();
  const create = useCreateCampaign();
  const update = useUpdateCampaign();
  const sendTest = useSendTestEmail();
  const schedule = useScheduleCampaign();
  const sendNow = useSendNow();
  const estimate = useEstimateRecipients();

  const [step, setStep] = useState<Step>('setup');

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [fromName, setFromName] = useState('Talbert Architectural');
  const [fromEmail, setFromEmail] = useState('newsletter@talbert.app');
  const [replyTo, setReplyTo] = useState('office@talbert.app');
  const [audienceIds, setAudienceIds] = useState<string[]>([]);
  const [excludeAudienceIds, setExcludeAudienceIds] = useState<string[]>([]);
  const [blocks, setBlocks] = useState<Block[]>(STARTER_BLOCKS);
  const [theme, setTheme] = useState<TemplateTheme>(DEFAULT_THEME);

  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testEmails, setTestEmails] = useState(user?.email ?? '');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');

  const [savedId, setSavedId] = useState<string | null>(null);
  const [recipientEstimate, setRecipientEstimate] = useState<number | null>(null);

  // Hydrate from server
  useEffect(() => {
    if (campaign) {
      setName(campaign.name);
      setSubject(campaign.subject);
      setPreviewText(campaign.previewText ?? '');
      setFromName(campaign.fromName);
      setFromEmail(campaign.fromEmail);
      setReplyTo(campaign.replyTo ?? '');
      setAudienceIds(campaign.audienceIds ?? []);
      setExcludeAudienceIds(campaign.excludeAudienceIds ?? []);
      setBlocks(campaign.blocks ?? STARTER_BLOCKS);
      setTheme(campaign.theme ?? DEFAULT_THEME);
      setSavedId(campaign.id);
    }
  }, [campaign]);

  const campaignId = savedId ?? (isNew ? null : id ?? null);
  const locked = campaign && !['draft'].includes(campaign.status);

  const readyStates = useMemo(() => ({
    setup:    !!(name.trim() && subject.trim() && fromName.trim() && fromEmail.trim()),
    design:   blocks.length > 0,
    audience: audienceIds.length > 0,
  }), [name, subject, fromName, fromEmail, blocks, audienceIds]);
  const isReady = readyStates.setup && readyStates.design && readyStates.audience;

  // Re-estimate recipients whenever audience selection changes
  useEffect(() => {
    if (audienceIds.length === 0) { setRecipientEstimate(null); return; }
    estimate.mutate({ audienceIds, excludeAudienceIds }, {
      onSuccess: (r) => setRecipientEstimate(r.count),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(audienceIds), JSON.stringify(excludeAudienceIds)]);

  async function saveDraft() {
    const payload = {
      name, subject, previewText, fromName, fromEmail, replyTo,
      audienceIds, excludeAudienceIds, blocks, theme,
    };
    try {
      if (campaignId) {
        await update.mutateAsync({ id: campaignId, ...payload });
        toast.success('Draft saved');
      } else {
        const result = await create.mutateAsync(payload);
        setSavedId(result.id);
        toast.success('Campaign created');
        navigate(`/newsletter/campaigns/${result.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  async function doSendTest() {
    if (!campaignId) { await saveDraft(); return; }
    const emails = testEmails.split(',').map((e) => e.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error('Enter at least one email address.'); return; }
    try {
      await sendTest.mutateAsync({ campaignId, toEmails: emails });
      toast.success(`Test sent to ${emails.length} recipient${emails.length === 1 ? '' : 's'}`);
      setTestModalOpen(false);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Test send failed.');
    }
  }

  async function doSchedule() {
    if (!campaignId) { toast.error('Save draft first'); return; }
    if (!scheduledFor) { toast.error('Pick a date and time.'); return; }
    try {
      await schedule.mutateAsync({
        id: campaignId,
        scheduledFor: new Date(scheduledFor).toISOString(),
      });
      toast.success(`Scheduled for ${new Date(scheduledFor).toLocaleString()}`);
      setScheduleModalOpen(false);
      navigate(`/newsletter/campaigns/${campaignId}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Schedule failed.');
    }
  }

  async function doSendNow() {
    if (!campaignId) { toast.error('Save draft first'); return; }
    if (!confirm(`Send this campaign now to ${recipientEstimate ?? '?'} recipients? This cannot be undone.`)) return;
    try {
      const r = await sendNow.mutateAsync(campaignId);
      toast.success(`Sending to ${r.recipientCount} subscribers now`);
      navigate(`/newsletter/campaigns/${campaignId}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Send failed.');
    }
  }

  if (!isNew && isLoading) return <LoaderScreen />;
  if (!isNew && error) {
    return (
      <Card>
        <EmptyState icon={AlertCircle}
          title="Could not load campaign"
          description={(error as { message?: string })?.message ?? 'Not found.'}
          action={<Link to="/newsletter/campaigns"><Button variant="secondary" leftIcon={<ArrowLeft size={14} />}>Back</Button></Link>}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Link to="/newsletter/campaigns" className="inline-flex items-center gap-2 text-xs text-text-dim hover:text-text-muted">
        <ArrowLeft size={12} /> <span className="kicker">All Campaigns</span>
      </Link>

      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <span className="kicker">Campaign</span>
            {campaign && <Badge tone="neutral">{CAMPAIGN_STATUS_LABELS[campaign.status]}</Badge>}
          </div>
          <h1 className="font-display text-3xl text-text truncate">
            {name || (isNew ? 'New campaign' : 'Untitled')}
          </h1>
        </div>

        {canWrite && !locked && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" leftIcon={<Eye size={13} />}
              onClick={() => { setTestEmails(user?.email ?? ''); setTestModalOpen(true); }}
              disabled={!isReady}
            >
              Send Test
            </Button>
            <Button variant="secondary" leftIcon={<Save size={13} />}
              onClick={saveDraft}
              loading={create.isPending || update.isPending}
            >
              Save Draft
            </Button>
            <Button variant="secondary" leftIcon={<Clock size={13} />}
              onClick={() => setScheduleModalOpen(true)}
              disabled={!isReady}
            >
              Schedule
            </Button>
            <Button leftIcon={<Send size={13} />}
              onClick={doSendNow}
              disabled={!isReady}
              loading={sendNow.isPending}
            >
              Send Now
            </Button>
          </div>
        )}
      </header>

      {/* Stepper */}
      <div className="flex items-center gap-2 flex-wrap">
        <StepPill active={step === 'setup'}    ready={readyStates.setup}    onClick={() => setStep('setup')}    label="1. Setup" />
        <ChevronRight size={12} className="text-text-dim" />
        <StepPill active={step === 'design'}   ready={readyStates.design}   onClick={() => setStep('design')}   label="2. Design" />
        <ChevronRight size={12} className="text-text-dim" />
        <StepPill active={step === 'audience'} ready={readyStates.audience} onClick={() => setStep('audience')} label="3. Audience" />
        <ChevronRight size={12} className="text-text-dim" />
        <StepPill active={step === 'review'}   ready={isReady ?? false}     onClick={() => setStep('review')}   label="4. Review" />
      </div>

      {/* Step content */}
      {step === 'setup' && (
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Campaign Name" required
              placeholder="April 2026 Newsletter"
              hint="Internal label — not visible to recipients."
              value={name} onChange={(e) => setName(e.target.value)}
              disabled={!!locked}
            />
            <Input label="Subject Line" required
              placeholder="Spring collection just arrived"
              value={subject} onChange={(e) => setSubject(e.target.value)}
              disabled={!!locked}
            />
            <Input label="Preview Text"
              placeholder="Shown next to subject in inbox previews"
              value={previewText} onChange={(e) => setPreviewText(e.target.value)}
              className="md:col-span-2" disabled={!!locked}
            />
            <Input label="From Name" required
              value={fromName} onChange={(e) => setFromName(e.target.value)}
              disabled={!!locked}
            />
            <Input label="From Email" type="email" required
              value={fromEmail} onChange={(e) => setFromEmail(e.target.value)}
              hint="Must be a verified SES address."
              disabled={!!locked}
            />
            <Input label="Reply-To" type="email"
              value={replyTo} onChange={(e) => setReplyTo(e.target.value)}
              hint="Where replies go. Optional."
              className="md:col-span-2" disabled={!!locked}
            />
          </div>
          {templates && templates.length > 0 && !locked && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="kicker mb-3">Or start from a template</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {templates.slice(0, 8).map((t) => (
                  <button key={t.id} type="button"
                    onClick={() => { setBlocks(t.blocks); setTheme(t.theme); if (t.subject) setSubject(t.subject); toast.success(`Loaded "${t.name}"`); }}
                    className="text-left p-3 border border-border rounded-sm hover:border-brass-400/60 hover:bg-bg-hover transition-colors"
                  >
                    <div className="text-sm text-text truncate">{t.name}</div>
                    <div className="text-2xs text-text-dim mt-0.5 truncate">{t.description ?? t.category ?? 'Template'}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {step === 'design' && (
        <EmailEditor
          blocks={blocks}
          theme={theme}
          onBlocksChange={setBlocks}
          onThemeChange={setTheme}
        />
      )}

      {step === 'audience' && (
        <Card>
          <div className="kicker mb-3">Send to</div>
          <AudienceSelector
            selected={audienceIds}
            onChange={setAudienceIds}
            audiences={audiences ?? []}
            disabled={!!locked}
          />
          <div className="mt-6 pt-6 border-t border-border">
            <div className="kicker mb-3">Exclude</div>
            <p className="text-xs text-text-muted mb-3">
              Subscribers in these audiences will NOT receive this campaign, even if they're in a Send-to audience.
            </p>
            <AudienceSelector
              selected={excludeAudienceIds}
              onChange={setExcludeAudienceIds}
              audiences={audiences ?? []}
              disabled={!!locked}
            />
          </div>
          {recipientEstimate !== null && (
            <div className="mt-6 pt-6 border-t border-brass-400/40 bg-brass-400/5 -mx-6 -mb-6 p-6 rounded-b-sm">
              <div className="flex items-center gap-3">
                <Users size={16} className="text-brass-300" />
                <div>
                  <div className="kicker text-brass-300">Estimated Recipients</div>
                  <div className="font-display text-2xl text-text tabular">{recipientEstimate.toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {step === 'review' && (
        <Card>
          <div className="kicker mb-4">Pre-flight Check</div>
          <div className="space-y-2">
            <ChecklistItem ok={!!readyStates.setup}
              label="Subject line and sender information set"
              hint={!readyStates.setup ? 'Complete the Setup step' : undefined}
            />
            <ChecklistItem ok={!!readyStates.design}
              label="Email design has at least one block"
              hint={!readyStates.design ? 'Add blocks in the Design step' : undefined}
            />
            <ChecklistItem ok={blocks.some((b) => b.type === 'footer')}
              label="Footer with unsubscribe link present"
              hint={!blocks.some((b) => b.type === 'footer') ? 'Footer is auto-added at send time if missing — but adding one explicitly gives you design control.' : undefined}
            />
            <ChecklistItem ok={!!readyStates.audience}
              label="At least one audience selected"
              hint={!readyStates.audience ? 'Pick recipients in the Audience step' : undefined}
            />
            <ChecklistItem ok={recipientEstimate !== null && recipientEstimate > 0}
              label={`${recipientEstimate?.toLocaleString() ?? 0} subscribers will receive this email`}
            />
          </div>

          {isReady && (
            <div className="mt-8 pt-6 border-t border-border flex flex-wrap gap-3">
              <Button leftIcon={<Eye size={13} />} variant="secondary"
                onClick={() => { setTestEmails(user?.email ?? ''); setTestModalOpen(true); }}
              >
                Send Test to Myself
              </Button>
              <Button leftIcon={<Clock size={13} />} variant="secondary"
                onClick={() => setScheduleModalOpen(true)}
              >
                Schedule for Later
              </Button>
              <Button leftIcon={<Send size={13} />}
                onClick={doSendNow}
                loading={sendNow.isPending}
              >
                Send Now to {recipientEstimate?.toLocaleString() ?? '?'} Subscribers
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Test send modal */}
      <Modal
        open={testModalOpen}
        onClose={() => setTestModalOpen(false)}
        kicker="Send Test"
        title="Preview in your inbox"
        description="Send a test version to yourself or colleagues. Merge tags will use sample data."
        footer={
          <>
            <Button variant="ghost" onClick={() => setTestModalOpen(false)}>Cancel</Button>
            <Button onClick={doSendTest} loading={sendTest.isPending} leftIcon={<Mail size={13} />}>
              Send Test
            </Button>
          </>
        }
      >
        <div className="py-2">
          <Input label="Recipient emails" type="text"
            placeholder="me@example.com, colleague@example.com"
            value={testEmails}
            onChange={(e) => setTestEmails(e.target.value)}
            hint="Comma-separated."
          />
        </div>
      </Modal>

      {/* Schedule modal */}
      <Modal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        kicker="Schedule Send"
        title="Pick a time to send"
        description="Campaign will be sent automatically at the chosen time. Use your local timezone."
        footer={
          <>
            <Button variant="ghost" onClick={() => setScheduleModalOpen(false)}>Cancel</Button>
            <Button onClick={doSchedule} loading={schedule.isPending} leftIcon={<Clock size={13} />}>
              Schedule
            </Button>
          </>
        }
      >
        <div className="py-2">
          <Input label="Send at" type="datetime-local"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}

// ───────────── Helper components ─────────────

function StepPill({ active, ready, onClick, label }: { active: boolean; ready: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={`
        px-4 h-9 text-xs uppercase tracking-[0.12em] rounded-sm border transition-colors
        ${active
          ? 'bg-brass-400/10 text-brass-300 border-brass-400/40'
          : ready
            ? 'bg-bg-panel text-text-muted border-border hover:border-border-strong'
            : 'bg-bg-subtle text-text-dim border-border'}
      `}
    >
      {ready && !active && <span className="text-success mr-1">✓</span>}
      {label}
    </button>
  );
}

function ChecklistItem({ ok, label, hint }: { ok: boolean; label: string; hint?: string }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className={`
        shrink-0 h-5 w-5 rounded-full flex items-center justify-center
        ${ok ? 'bg-success/15 text-success' : 'bg-bg-subtle text-text-dim'}
      `}>
        {ok ? '✓' : '·'}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${ok ? 'text-text' : 'text-text-muted'}`}>{label}</div>
        {hint && <div className="text-2xs text-text-dim mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function AudienceSelector({
  selected, onChange, audiences, disabled,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
  audiences: Array<{ id: string; name: string; description?: string; subscriberCount?: number; kind?: string }>;
  disabled?: boolean;
}) {
  if (audiences.length === 0) {
    return (
      <div className="text-sm text-text-dim italic">
        No audiences defined. Create one in the Audiences tab to target a specific group.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {audiences.map((a) => {
        const isSelected = selected.includes(a.id);
        return (
          <button key={a.id} type="button" disabled={disabled}
            onClick={() => onChange(isSelected ? selected.filter((x) => x !== a.id) : [...selected, a.id])}
            className={`
              text-left p-3 border rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed
              ${isSelected
                ? 'border-brass-400 bg-brass-400/5'
                : 'border-border hover:border-border-strong hover:bg-bg-hover'}
            `}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm text-text">{a.name}</div>
                {a.description && <div className="text-2xs text-text-dim mt-0.5 truncate">{a.description}</div>}
              </div>
              {isSelected && <div className="shrink-0 text-brass-300">✓</div>}
            </div>
            <div className="mt-2 flex items-center gap-3 text-2xs text-text-dim">
              <span>{a.kind === 'dynamic' ? 'Dynamic' : 'Static'}</span>
              {a.subscriberCount !== undefined && (
                <span className="tabular">{a.subscriberCount.toLocaleString()} subscribers</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Suppress unused for template typing warning
void ({} as Campaign);

// ───────────── Starter blocks for new campaigns ─────────────

const STARTER_BLOCKS: Block[] = [
  { id: crypto.randomUUID(), type: 'heading',
    content: { text: 'Hello {{firstName}},', level: 2, align: 'left' } },
  { id: crypto.randomUUID(), type: 'text',
    content: { text: "Write your message here. This is a great place to share news, announcements, or seasonal greetings with your customers.", align: 'left' } },
  { id: crypto.randomUUID(), type: 'button',
    content: { text: 'Learn More', url: 'https://talbertusa.com', align: 'center' } },
  { id: crypto.randomUUID(), type: 'footer',
    content: {
      companyName: 'Talbert Architectural Panels & Doors, Inc.',
      address: 'City of Industry, CA',
      fscLine: 'FSC® C014235',
    } },
];
